import Handlebars from 'handlebars';
import { transpileToHandlebars } from './transpile';
import { resolveToken, evaluateCondition } from './resolveToken';
import { flattenRecordIntoSection } from './buildPreviewDefaults';

function escapeHtml(str) {
  return String(str)
    .split('&')
    .join('&amp;')
    .split('<')
    .join('&lt;')
    .split('>')
    .join('&gt;');
}

function fieldErrorSpan(name) {
  return `<span class="lm-token-error" title="Unknown merge field">{{${escapeHtml(
    name
  )}}}</span>`;
}

function eachErrorSpan(name) {
  return `<span class="lm-token-error" title="Unknown collection">{{#each ${escapeHtml(
    name
  )}}}</span>`;
}

const key = (name) => String(name).toLowerCase().trim();
const leafKey = (path) => String(path).split('.').pop();

export function renderTemplate(html, context, index) {
  const ctx = context || {};
  const out = {
    html: '',
    resolutions: [],
    missing: [],
    errorTokens: [],
    errors: []
  };

  const { code, errors: transpileErrors } = transpileToHandlebars(html);
  for (const e of transpileErrors) {
    if (e.type === 'bad-condition') {
      out.errors.push(
        `Could not understand the condition {{#if ${e.condText}}}`
      );
    }
  }

  const hb = Handlebars.create();
  const collectionStack = [];

  // Resolve a token name against the current scope: the innermost FOR-EACH
  // item first (loop scope), then the top-level catalogue (fallthrough).
  const lookupInScope = (name, itemContext) => {
    const col = collectionStack[collectionStack.length - 1];
    if (col && col.itemsByToken) {
      const itemDesc = col.itemsByToken.get(key(name));
      if (itemDesc) {
        return {
          descriptor: itemDesc,
          raw:
            itemContext == null
              ? undefined
              : itemContext[leafKey(itemDesc.path)],
          viaItem: true
        };
      }
    }
    const d = index.topLevel.get(key(name));
    if (d) {
      return { descriptor: d, raw: (ctx.values || {})[d.path], viaItem: false };
    }
    return null;
  };

  hb.registerHelper('lmField', function lmField(name) {
    const hit = lookupInScope(name, this);
    if (!hit) {
      // Fail-closed: visible error, never a silent blank.
      out.errorTokens.push(name);
      return new hb.SafeString(fieldErrorSpan(name));
    }
    const res = resolveToken(hit.descriptor, hit.raw);
    out.resolutions.push({
      token: hit.descriptor.token,
      path: hit.descriptor.path,
      viaItem: hit.viaItem,
      ...res
    });
    if (res.state === 'resolved') {
      return new hb.SafeString(
        escapeHtml(res.display != null ? res.display : res.text)
      );
    }
    if (res.state === 'error') {
      out.errorTokens.push(name);
      return new hb.SafeString(fieldErrorSpan(name));
    }
    // empty | unknown → omit (no assertion, no literal token)
    out.missing.push({ token: hit.descriptor.token, state: res.state });
    return '';
  });

  hb.registerHelper('lmIf', function lmIf(...args) {
    const options = args[args.length - 1];
    const field = args[0];
    const op = args[1];
    const value = args.length > 3 ? args[2] : undefined;
    const cond = { field, op, value };

    let satisfied = false;
    const col = index.collections.get(key(field));
    if (col) {
      const items = (ctx.collections || {})[col.token] || [];
      satisfied = evaluateCondition(cond, col, {
        isCollection: true,
        length: items.length
      });
    } else {
      const hit = lookupInScope(field, this);
      if (!hit) {
        // Unknown condition field — fail closed to false + record it.
        out.errorTokens.push(field);
        satisfied = false;
      } else {
        const res = resolveToken(hit.descriptor, hit.raw);
        satisfied = evaluateCondition(cond, hit.descriptor, res);
      }
    }
    return satisfied ? options.fn(this) : options.inverse(this);
  });

  hb.registerHelper('lmEach', function lmEach(collectionName, options) {
    const col = index.collections.get(key(collectionName));
    if (!col) {
      out.errorTokens.push(collectionName);
      return new hb.SafeString(eachErrorSpan(collectionName));
    }
    const items = (ctx.collections || {})[col.token] || [];
    let html2 = '';
    collectionStack.push(col);
    for (const item of items) html2 += options.fn(item);
    collectionStack.pop();
    return html2;
  });

  try {
    const template = hb.compile(code);
    out.html = template({});
  } catch (e) {
    out.errors.push(e && e.message ? e.message : String(e));
    out.html = '';
  }

  return out;
}

export const AMBIGUOUS_SECTIONS = Object.freeze({
  Diseases: {
    collectionToken: 'Diagnoses',
    label: 'diagnosis',
    primaryField: 'diseaseName',
    dateFields: ['dateReported']
  },
  Genotypes: {
    collectionToken: 'Variants',
    label: 'genotype / variant',
    primaryField: 'geneName',
    dateFields: ['dateRecorded']
  },
  Phenotypes: {
    collectionToken: 'Phenotypes',
    label: 'phenotype',
    primaryField: 'phenotypeName',
    dateFields: ['dateRecorded']
  },
  Tests: {
    collectionToken: 'Tests',
    label: 'test',
    primaryField: 'testName',
    dateFields: ['dateRequested', 'dateResultReceived']
  }
});

export function findAmbiguousSections(resolutions, collections) {
  const found = new Map();
  for (const r of resolutions || []) {
    if (r.viaItem) continue;
    const section = String(r.path).split('.')[0];
    const meta = AMBIGUOUS_SECTIONS[section];
    if (!meta || found.has(section)) continue;
    const items = (collections || {})[meta.collectionToken] || [];
    if (items.length > 1) found.set(section, { section, ...meta, items });
  }
  return Array.from(found.values());
}

export async function resolveEditorContent(editor, content) {
  const resolveContext = editor._lmResolveContext;
  const catalogueIndex = editor._lmCatalogueIndex;
  if (!resolveContext || !catalogueIndex)
    return { html: content, result: null };

  let result = renderTemplate(content, resolveContext, catalogueIndex);
  const ambiguous = findAmbiguousSections(
    result.resolutions,
    resolveContext.collections
  );

  if (ambiguous.length) {
    const resolveAmbiguity = editor._lmResolveAmbiguity;
    if (resolveAmbiguity) {
      const choices = await resolveAmbiguity(ambiguous);
      if (!choices) return null;

      let values = resolveContext.values;
      for (const { section } of ambiguous) {
        if (choices[section]) {
          values = flattenRecordIntoSection(values, section, choices[section]);
        }
      }
      result = renderTemplate(
        content,
        { ...resolveContext, values },
        catalogueIndex
      );
    }
  }
  return { html: result.html, result };
}

export async function renderEditorContentResolved(editor, content) {
  const resolved = await resolveEditorContent(editor, content);
  if (resolved === null) return null;
  return resolved.html;
}
